import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import api, { onSessionExpired } from '@/shared/api/api-client';
import { logger } from '@/utils/logger';

export interface AuthUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_admin: boolean;
  admin_role: string | null;
  licence_id: string;
  subscription_date: string;
  subscription_status: string;
  last_login_date: string | null;
  /** Profile photo (#579): admins only, null when none was uploaded. */
  avatar_url: string | null;
}

export interface Licence {
  id: string;
  name: string;
  /** Licence ownership was removed (#647): always null. */
  owner_id: string | null;
  opening_date: string;
  status: string;
  user_count: number;
  created_at: string;
  updated_at: string;
}

/** Step 1 response from POST /auth/login */
export interface OtpRequired {
  requires_otp: true;
  otp_token: string;
  email: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  licence: Licence | null;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  /** Replace the cached current licence (e.g. after the owner renames it). */
  applyLicence: (licence: Licence) => void;
  /**
   * Commit an established session (user + licence) after the login slice
   * finishes its two-step flow. The network login now lives in that slice's
   * gateway; this only writes the app-wide session so guards/layout react.
   * Analogous to `applyLicence`.
   */
  applySession: (user: AuthUser, licence: Licence) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [licence, setLicence] = useState<Licence | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount, check if we have a valid session cookie
  useEffect(() => {
    api
      .get<AuthUser>('/auth/me')
      .then((data) => {
        setUser(data);
        return api.get<Licence>('/licences/current');
      })
      .then((licData) => setLicence(licData))
      .catch(() => {
        setUser(null);
        setLicence(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Register auto-logout when session expires (401 + refresh fails)
  useEffect(() => {
    onSessionExpired(() => {
      setUser(null);
      setLicence(null);
      logger.info('Session expired, auto-logout triggered');
    });
  }, []);

  // The two-step login flow (validate credentials → verify OTP) now lives in the
  // `features/auth/login` slice; its saga owns the network calls and the page
  // commits the resulting session here via `applySession`.

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore: clear state anyway
    }
    setUser(null);
    setLicence(null);
    logger.info('User logged out');
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const data = await api.get<AuthUser>('/auth/me');
      setUser(data);
    } catch {
      setUser(null);
      setLicence(null);
    }
  }, []);

  // Replace the cached licence in place. The rename network call now lives in the
  // My Licence slice's gateway; this only keeps the app-wide copy in sync so other
  // screens (e.g. list export) reflect the new name without a reload.
  const applyLicence = useCallback((updated: Licence) => setLicence(updated), []);

  // Commit a session established by the login slice (user + its current licence).
  const applySession = useCallback((nextUser: AuthUser, nextLicence: Licence) => {
    setUser(nextUser);
    setLicence(nextLicence);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: user !== null,
      // The client "owner" role (#647): the same rule the server applies to
      // renaming a licence (edit-policy, requireOwner), not `licences.owner_id`.
      isOwner: user?.role.toLowerCase() === 'owner',
      isAdmin: Boolean(user?.is_admin),
      isLoading,
      user,
      licence,
      logout,
      refreshUser,
      applyLicence,
      applySession,
    }),
    [user, licence, isLoading, logout, refreshUser, applyLicence, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
